import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray } from '@/types/shared';

/**
 * `GET /customers/:id/contacts/options` — the signatory contacts a contract can
 * be attached to. Each option carries `contact_roles`, which the create form
 * uses to gate the `supplier` contract role (legacy `ContactSelect` afterFetch →
 * `selectedContactRoles`).
 */

/** A raw contact option row from the backend. */
interface ContactOptionRow {
  customer_contact_id: number;
  contact_name: string;
  contact_label?: string[];
  contact_roles?: string[];
}

/** A contact option consumed by the picker. */
export interface ContactOption {
  value: number;
  label: string;
  contact_roles: string[];
}

function toOption(r: ContactOptionRow): ContactOption {
  const labels = (r.contact_label ?? []).filter(Boolean);
  return {
    value: r.customer_contact_id,
    label: labels.length ? `${r.contact_name} (${labels.join(', ')})` : r.contact_name,
    contact_roles: r.contact_roles ?? [],
  };
}

/** Fetch a customer's contact options (empty while no customer is selected). */
export function useCustomerContacts(customerId: number) {
  return useQuery({
    queryKey: ['customers', customerId, 'contacts', 'options'],
    enabled: !!customerId,
    queryFn: async (): Promise<ContactOption[]> => {
      const res = await dataCentralApi.get<ResponseArray<ContactOptionRow>>(
        `/customers/${customerId}/contacts/options`,
      );
      return (res.data.results ?? []).map(toOption);
    },
  });
}
